/**
 * Authentication Service
 * Handles authentication API calls and Supabase auth operations
 */

import {
  signIn as supabaseSignIn,
  signUp as supabaseSignUp,
  signOut as supabaseSignOut,
  resetPassword as supabaseResetPassword,
  updatePassword as supabaseUpdatePassword,
} from '../lib/supabase';
import type { LoginCredentials, SignupCredentials } from '../types/auth.types';

/**
 * Sign in with email and password
 */
export async function login(credentials: LoginCredentials) {
  try {
    const { email, password } = credentials;
    const data = await supabaseSignIn(email, password);
    return { success: true, data };
  } catch (error: any) {
    console.error('Login error:', error);
    return {
      success: false,
      error: error.message || 'Failed to sign in',
    };
  }
}

/**
 * Sign up with email and password
 */
export async function signup(credentials: SignupCredentials) {
  try {
    const { email, password } = credentials;
    const data = await supabaseSignUp(email, password);
    return { success: true, data };
  } catch (error: any) {
    console.error('Signup error:', error);
    return {
      success: false,
      error: error.message || 'Failed to sign up',
    };
  }
}

/**
 * Sign out
 */
export async function logout() {
  try {
    await supabaseSignOut();
    return { success: true };
  } catch (error: any) {
    console.error('Logout error:', error);
    return {
      success: false,
      error: error.message || 'Failed to sign out',
    };
  }
}

/**
 * Request password reset
 */
export async function requestPasswordReset(email: string) {
  try {
    await supabaseResetPassword(email);
    return {
      success: true,
      message: 'Password reset email sent',
    };
  } catch (error: any) {
    console.error('Password reset error:', error);
    return {
      success: false,
      error: error.message || 'Failed to send password reset email',
    };
  }
}

/**
 * Update password
 */
export async function updateUserPassword(newPassword: string) {
  try {
    await supabaseUpdatePassword(newPassword);
    return {
      success: true,
      message: 'Password updated successfully',
    };
  } catch (error: any) {
    console.error('Update password error:', error);
    return {
      success: false,
      error: error.message || 'Failed to update password',
    };
  }
}
